import { useState, useEffect } from "react";
import { db } from "../../firebase";
import { doc, query, collectionGroup, where, onSnapshot, getDocs } from "firebase/firestore";
import { format } from "date-fns";
import { FB_COLLECTION_PATH, WORKFLOW_WERKER_TYPES } from "../../functions/postActionTypes";

export interface SchedJobData {
  JobDocId: string;
  ShiftIdentifier: string;
  SchedUid: string;
  DateDay: string;
  EndDay: string;
  StartDateTime: string;
  FinishDateTime: string;
  StartDateTimeRaw: string;
  FinishDateTimeRaw: string;
  Company: string;
  Location: string;
  LocationCoordinates: string;
  Pay: string;
  ShiftNotes: string;
  NumJobSlots: number;
  JobStatusSched: string;
  JobStatusWerker: string;
  IsPaidJob: boolean;
  PaidWerker: boolean;
  PaidWerkers: boolean;
  JobCancelledByScheduler: boolean;
  CreatedAt: number;
  UpdatedAt: number;
  IsRead: boolean;
  IsNewMessage: boolean;
  scheduledCount: number;
  invitedCount: number;
  seriesId?: string | null;
  seriesIndex?: number | null;
  seriesTotal?: number | null;
}

export interface JobData {
  JobDocId: string;
  MyJobDocId?: string;
  WerkerUid: string;
  WerkerFirstName: string;
  WerkerLastName: string;
  WerkerProfilePicURL: string;
  SchedulerUid: string;
  SchedFirstName: string;
  SchedLastName: string;
  SchedProfilePic: string;
  SchedUid?: string;
  TotalJobSlots: number;
  UniqueJobDocId: string;
  JobIdentifier: string;
  Pay: string;
  Company: string;
  StartDate: string;
  EndDate: string;
  Location: string;
  LocationCoordinates: string;
  Notes: string;
  IsPaidJob: boolean;
  WerkersPaid: boolean;
  SchedJobStatus: string;
  WerkerJobStatus: string;
  PaidWerker: boolean;
  IsNewMessage: boolean;
  IsCancelled: boolean;
  ScheduledCount: number;
}

interface UseJobDataProps {
  jobId: string | null;
  userId: string;
  isScheduler: boolean;
  profileUserId?: string; // For filtering werkers list
}

/**
 * Hook for fetching and transforming job data from Firestore
 * Handles both scheduler and werker views with real-time updates
 */
export const useJobData = ({ jobId, userId, isScheduler, profileUserId }: UseJobDataProps) => {
  const [jobData, setJobData] = useState<JobData | null>(null);
  const [schedJobData, setSchedJobData] = useState<SchedJobData | null>(null);
  const [scheduledWerkers, setScheduledWerkers] = useState<JobData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jobId) {
      setJobData(null);
      setSchedJobData(null);
      setScheduledWerkers([]);
      return;
    }

    setLoading(true);

    let unsubscribe: (() => void) | undefined;

    try {
      if (isScheduler) {
        // Scheduler view: use collectionGroup query
        const schedJobQuery = query(
          collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
          where("jobInfo.core.uniqueJobId", "==", jobId),
          where("docStamp.userInfoFrom.uid", "==", userId),
          where("docStamp.userInfoTo.uid", "==", userId)
        );

        unsubscribe = onSnapshot(schedJobQuery, (querySnapshot) => {
          if (!querySnapshot.empty) {
            querySnapshot.forEach((docSnap) => {
              const jobDoc: any = docSnap.data();

              const schedData: SchedJobData = {
                JobDocId: jobDoc.jobInfo.core.uniqueJobId,
                ShiftIdentifier: jobDoc.jobInfo.core.shiftIdentifier || "",
                SchedUid: jobDoc.docStamp.userInfoFrom.uid,
                DateDay: jobDoc.jobInfo.core.dateTimeStart
                  ? format(new Date(jobDoc.jobInfo.core.dateTimeStart.substring(0, 19)), "M/d/yy")
                  : "",
                EndDay: jobDoc.jobInfo.core.dateTimeFinish
                  ? format(new Date(jobDoc.jobInfo.core.dateTimeFinish.substring(0, 19)), "M/d/yy")
                  : "",
                StartDateTime: jobDoc.jobInfo.core.dateTimeStart
                  ? format(new Date(jobDoc.jobInfo.core.dateTimeStart.substring(0, 19)), "h:mma")
                  : "",
                FinishDateTime: jobDoc.jobInfo.core.dateTimeFinish
                  ? format(new Date(jobDoc.jobInfo.core.dateTimeFinish.substring(0, 19)), "h:mma")
                  : "",
                StartDateTimeRaw: jobDoc.jobInfo.core.dateTimeStart,
                FinishDateTimeRaw: jobDoc.jobInfo.core.dateTimeFinish,
                Company: jobDoc.jobInfo.core.company,
                Location: jobDoc.jobInfo.core.location,
                LocationCoordinates: jobDoc.jobInfo.core.locationCoordinates,
                Pay: jobDoc.jobInfo.core.pay,
                ShiftNotes: jobDoc.jobInfo.core.shiftNotes,
                NumJobSlots: jobDoc.jobInfo.core.numJobSlots,
                JobStatusSched: jobDoc.jobInfo.status.jobStatusSched,
                JobStatusWerker: jobDoc.jobInfo.status.jobStatusWerker,
                IsPaidJob: jobDoc.jobInfo.status.isPaidJob,
                PaidWerker: jobDoc.jobInfo.status.isPaidWerker,
                PaidWerkers: jobDoc.jobInfo.status.isPaidWerkers,
                JobCancelledByScheduler: jobDoc.jobInfo.status.jobCancelledByScheduler,
                CreatedAt: jobDoc.docStamp.timestamp.createdAt,
                UpdatedAt: jobDoc.docStamp.timestamp.updatedAt,
                IsRead: jobDoc.jobInfo.status.isRead,
                IsNewMessage: jobDoc.jobInfo.status.isNewMessage,
                scheduledCount: jobDoc.jobInfo.status.scheduledCount ?? 0,
                invitedCount: jobDoc.jobInfo.status.invitedCount ?? 0,
                seriesId: (jobDoc.jobInfo?.core?.seriesId as string) ?? (jobDoc.seriesId as string) ?? null,
                seriesIndex: (jobDoc.jobInfo?.core?.seriesIndex as number) ?? (jobDoc.seriesIndex as number) ?? null,
                seriesTotal: (jobDoc.jobInfo?.core?.seriesTotal as number) ?? (jobDoc.seriesTotal as number) ?? null,
              };

              setSchedJobData(schedData);
              setLoading(false);
            });
          } else {
            setLoading(false);
          }
        });
      } else {
        // Werker/Desktop view: use direct doc reference
        const jobDocRef = doc(db, "jobsDocs", jobId);

        unsubscribe = onSnapshot(jobDocRef, async (jobDocSnap) => {
          try {
            if (jobDocSnap.exists()) {
              const data = jobDocSnap.data();

              const jobInfo: JobData = {
                JobDocId: isScheduler ? jobId : (data.jobInfo?.core?.uniqueJobId || ""),
                MyJobDocId: !isScheduler ? jobId : undefined,
                WerkerUid: data.docStamp?.userInfoTo?.uid || "",
                WerkerFirstName: data.docStamp?.userInfoTo?.firstName || "",
                WerkerLastName: data.docStamp?.userInfoTo?.lastName || "",
                WerkerProfilePicURL: data.docStamp?.userInfoTo?.profilePicURL || "",
                SchedulerUid: data.docStamp?.userInfoFrom?.uid || "",
                SchedFirstName: data.docStamp?.userInfoFrom?.firstName || "",
                SchedLastName: data.docStamp?.userInfoFrom?.lastName || "",
                SchedProfilePic: data.docStamp?.userInfoFrom?.profilePicURL || "",
                SchedUid: data.docStamp?.userInfoFrom?.uid || "",
                TotalJobSlots: data.jobInfo?.core?.numJobSlots || 0,
                UniqueJobDocId: data.jobInfo?.core?.uniqueJobId || "",
                JobIdentifier: data.jobInfo?.core?.shiftIdentifier || "",
                Pay: data.jobInfo?.core?.pay || "",
                Company: data.jobInfo?.core?.company || "",
                StartDate: data.jobInfo?.core?.dateTimeStart || "",
                EndDate: data.jobInfo?.core?.dateTimeFinish || "",
                Location: data.jobInfo?.core?.location || "",
                LocationCoordinates: data.jobInfo?.core?.locationCoordinates || "",
                Notes: data.jobInfo?.core?.shiftNotes || "",
                IsPaidJob: data.jobInfo?.status?.isPaidJob || false,
                WerkersPaid: data.jobInfo?.status?.isPaidWerkers || false,
                SchedJobStatus: data.jobInfo?.status?.jobStatusSched || "",
                WerkerJobStatus: data.jobInfo?.status?.jobStatusWerker || "",
                PaidWerker: data.jobInfo?.status?.isPaidWerker || false,
                IsNewMessage: data.jobInfo?.status?.isNewMessage || false,
                IsCancelled: data.jobInfo?.status?.jobCancelledByScheduler || false,
                ScheduledCount: data.jobInfo?.status?.scheduledCount || 0,
              };

              setJobData(jobInfo);

              // For scheduler view, also populate SchedJobData
              if (isScheduler) {
                const schedData: SchedJobData = {
                  JobDocId: data.jobInfo?.core?.uniqueJobId || "",
                  ShiftIdentifier: data.jobInfo?.core?.shiftIdentifier || "",
                  SchedUid: data.docStamp?.userInfoFrom?.uid || "",
                  DateDay: data.jobInfo?.core?.dateTimeStart
                    ? format(new Date(data.jobInfo.core.dateTimeStart.substring(0, 19)), "M/d/yy")
                    : "",
                  EndDay: data.jobInfo?.core?.dateTimeFinish
                    ? format(new Date(data.jobInfo.core.dateTimeFinish.substring(0, 19)), "M/d/yy")
                    : "",
                  StartDateTime: data.jobInfo?.core?.dateTimeStart
                    ? format(new Date(data.jobInfo.core.dateTimeStart.substring(0, 19)), "h:mma")
                    : "",
                  FinishDateTime: data.jobInfo?.core?.dateTimeFinish
                    ? format(new Date(data.jobInfo.core.dateTimeFinish.substring(0, 19)), "h:mma")
                    : "",
                  StartDateTimeRaw: data.jobInfo?.core?.dateTimeStart || "",
                  FinishDateTimeRaw: data.jobInfo?.core?.dateTimeFinish || "",
                  Company: data.jobInfo?.core?.company || "",
                  Location: data.jobInfo?.core?.location || "",
                  LocationCoordinates: data.jobInfo?.core?.locationCoordinates || "",
                  Pay: data.jobInfo?.core?.pay || "",
                  ShiftNotes: data.jobInfo?.core?.shiftNotes || "",
                  NumJobSlots: data.jobInfo?.core?.numJobSlots || 0,
                  JobStatusSched: data.jobInfo?.status?.jobStatusSched || "",
                  JobStatusWerker: data.jobInfo?.status?.jobStatusWerker || "",
                  IsPaidJob: data.jobInfo?.status?.isPaidJob || false,
                  PaidWerker: data.jobInfo?.status?.isPaidWerker || false,
                  PaidWerkers: data.jobInfo?.status?.isPaidWerkers || false,
                  JobCancelledByScheduler: data.jobInfo?.status?.jobCancelledByScheduler || false,
                  CreatedAt: data.docStamp?.timestamp?.createdAt || 0,
                  UpdatedAt: data.docStamp?.timestamp?.updatedAt || 0,
                  IsRead: data.jobInfo?.status?.isRead || false,
                  IsNewMessage: data.jobInfo?.status?.isNewMessage || false,
                  scheduledCount: data.jobInfo?.status?.scheduledCount || 0,
                  seriesId: data.jobInfo?.core?.seriesId || data.seriesId || null,
                  seriesIndex: data.jobInfo?.core?.seriesIndex || data.seriesIndex || null,
                  seriesTotal: data.jobInfo?.core?.seriesTotal || data.seriesTotal || null,
                };

                setSchedJobData(schedData);
              }

              // For werker view, fetch other scheduled werkers
              if (!isScheduler && jobInfo.UniqueJobDocId && jobInfo.SchedulerUid && profileUserId) {
                const werkersQuery = query(
                  collectionGroup(db, "jobsDocs"),
                  where("jobInfo.core.uniqueJobId", "==", jobInfo.UniqueJobDocId),
                  where("docStamp.userInfoFrom.uid", "==", jobInfo.SchedulerUid),
                  where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Scheduled)
                );

                const werkersSnap = await getDocs(werkersQuery);
                const werkers: JobData[] = [];
                werkersSnap.forEach((doc) => {
                  const d = doc.data();
                  if (d.docStamp?.userInfoTo?.uid !== profileUserId) {
                    werkers.push({
                      JobDocId: d.jobInfo?.core?.uniqueJobId || "",
                      WerkerUid: d.docStamp?.userInfoTo?.uid || "",
                      WerkerFirstName: d.docStamp?.userInfoTo?.firstName || "",
                      WerkerLastName: d.docStamp?.userInfoTo?.lastName || "",
                      WerkerProfilePicURL: d.docStamp?.userInfoTo?.profilePicURL || "",
                      SchedulerUid: d.docStamp?.userInfoFrom?.uid || "",
                      SchedFirstName: d.docStamp?.userInfoFrom?.firstName || "",
                      SchedLastName: d.docStamp?.userInfoFrom?.lastName || "",
                      SchedProfilePic: d.docStamp?.userInfoFrom?.profilePicURL || "",
                      TotalJobSlots: d.jobInfo?.core?.numJobSlots || 0,
                      UniqueJobDocId: d.jobInfo?.core?.uniqueJobId || "",
                      JobIdentifier: d.jobInfo?.core?.shiftIdentifier || "",
                      Pay: d.jobInfo?.core?.pay || "",
                      Company: d.jobInfo?.core?.company || "",
                      StartDate: d.jobInfo?.core?.dateTimeStart || "",
                      EndDate: d.jobInfo?.core?.dateTimeFinish || "",
                      Location: d.jobInfo?.core?.location || "",
                      LocationCoordinates: d.jobInfo?.core?.locationCoordinates || "",
                      Notes: d.jobInfo?.core?.shiftNotes || "",
                      IsPaidJob: d.jobInfo?.status?.isPaidJob || false,
                      WerkersPaid: d.jobInfo?.status?.isPaidWerkers || false,
                      SchedJobStatus: d.jobInfo?.status?.jobStatusSched || "",
                      WerkerJobStatus: d.jobInfo?.status?.jobStatusWerker || "",
                      PaidWerker: d.jobInfo?.status?.isPaidWerker || false,
                      IsNewMessage: d.jobInfo?.status?.isNewMessage || false,
                      IsCancelled: d.jobInfo?.status?.jobCancelledByScheduler || false,
                      ScheduledCount: d.jobInfo?.status?.scheduledCount || 0,
                    });
                  }
                });
                setScheduledWerkers(werkers);
              }

              setLoading(false);
            } else {
              setLoading(false);
            }
          } catch (error) {
            console.error("Error in job data snapshot:", error);
            setLoading(false);
          }
        });
      }
    } catch (error) {
      console.error("Error setting up job data listener:", error);
      setLoading(false);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [jobId, userId, isScheduler, profileUserId]);

  return {
    jobData,
    schedJobData,
    scheduledWerkers,
    loading,
  };
};
